<template>
    <div class="items" :style="{ pointerEvents: itemListReady ? 'auto' : 'none' }">
        <template v-if="showMainItemList">
            <div class="top-bar">
                <div class="search" v-if="selectedOrderType?.showSearchBar !== false">
                    <SvgIcon name="search" class="search-icon" width="1.5rem" height="1.5rem" />
                    <input v-model="searchQuery" type="text" class="search-bar" :class="{ 'search-active': isSearching }"
                        placeholder="Search" @focus="handleFocus" @blur="handleBlur" @input="updateSearchQuery" />
                </div>
                <div class="button-group" v-if="!isSearching">
                    <button v-for="ot in orderTypes" :key="ot.type" class="btn"
                        :class="{ 'selected': selectedOrderType === ot, 'btn-secondary': selectedOrderType !== ot.type }"
                        @click="selectOrderType(ot)">
                        {{ ot.name }}
                    </button>
                </div>
                <div class="button-group" v-else>
                    <button @mousedown="handleCancel">Cancel</button>
                </div>
                <button
                    v-if="groceryCheckoutModeEnabled"
                    class="grocery-close-button"
                    @click="closeGroceryItemList"
                >
                    Close
                </button>
            </div>
            <div v-if="isSearching">
                <div class="item-list">
                    <div v-for="item in filteredItems" :key="item.ref"
                        :class="['item', { 'no-image': !item.images.length, 'sold-out': isSoldOut(item) }]" @click="itemSelected(item)">
                        <div class="item-content">
                            <div v-if="item.images.length"
                                :style="{ backgroundImage: 'url(' + imageUrl(item.images[0]) + ')' }" class="item-image-bg">
                            </div>
                            <div v-else class="color-block">
                                <span class="item-name-block">{{ item.name }}</span>
                            </div>
                            <span class="item-name">{{ item.name }}</span>
                            <span class="item-price">{{ formatCurrency(item.price) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <transition v-else name="fade" mode="out-in">
                <div class="item-list" v-if="localSelectedDepartment" :key="localSelectedDepartment.ref">
                    <div v-for="item in browseItems" :key="item.ref"
                        :class="['item', { 'no-image': !item.images.length, 'sold-out': isSoldOut(item) }]" @click="itemSelected(item)">
                        <div class="item-content">
                            <div v-if="item.images.length"
                                :style="{ backgroundImage: 'url(' + imageUrl(item.images[0]) + ')' }" class="item-image-bg">
                            </div>
                            <div v-else class="color-block">
                                <span class="item-name-block">{{ item.name }}</span>
                            </div>
                            <span class="item-name">{{ item.name }}</span>
                            <span class="item-price">{{ formatCurrency(item.price) }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </template>
        <div v-else class="grocery-mode-panel">
            <button class="grocery-mode-icon-button" @click="handleGroceryIconTap">
                <SvgIcon name="barcode-scanner" width="8rem" height="8rem" />
            </button>
            <h2>Scan Items</h2>
            <p>Scan each item barcode to add it to your cart.</p>
        </div>
    </div>
</template>

<script>
import { imageUrl, formatCurrency } from "@/utils/helpers";
import SvgIcon from '@/components/global/SvgIcon.vue';

export default {
    components: {
        SvgIcon
    },
    data() {
        return {
            isSearching: false,
            searchQuery: '',
            filteredItems: null,
            localSelectedDepartment: this.selectedDepartment,
            scannerTapCount: 0,
            showMainItemListForGrocery: false,
            itemListReady: true,
        }
    },
    props: {
        selectedDepartment: {
            type: Object,
            required: false
        },
        items: {
            type: Array,
            required: true
        },
        itemsToSearch: {
            type: Array,
            required: false
        },
        departmentItems: {
            type: Function,
            required: true
        },
        orderTypes: {
            type: Array,
            required: true,
        },
        selectedOrderType: {
            type: Object,
            required: true
        }
    },
    computed: {
        groceryCheckoutModeEnabled() {
            return this.selectedOrderType?.view === 'retail' && this.selectedOrderType?.groceryCheckoutMode === true;
        },
        showMainItemList() {
            return !this.groceryCheckoutModeEnabled || this.showMainItemListForGrocery;
        },
        browseItems() {
            if (this.groceryCheckoutModeEnabled) {
                const allItems = this.itemsToSearch || this.items || [];
                return [...allItems].sort((a, b) => a.name.localeCompare(b.name));
            }
            if (!this.localSelectedDepartment) return [];
            return this.departmentItems(this.localSelectedDepartment) || [];
        }
    },
    mounted() {
        this.filteredItems = this.itemsToSearch || this.items;
        this.filteredItems = this.filteredItems.sort((a, b) => a.name.localeCompare(b.name));
    },
    watch: {
        selectedDepartment(newVal) {
            this.localSelectedDepartment = newVal;
        },
        groceryCheckoutModeEnabled(enabled) {
            if (!enabled) {
                this.showMainItemListForGrocery = false;
                this.scannerTapCount = 0;
            } else {
                this.isSearching = false;
                this.searchQuery = '';
                this.$emit('setBarcodeActive', true);
            }
        }
    },
    methods: {
        imageUrl,
        formatCurrency,
        isSoldOut(item) {
            return item?.stockStatus === 'OUT_OF_STOCK';
        },
        selectOrderType(type) {
            this.$router.push({ name: 'start', query: { orderTypeRef: type.id } });
        },
        itemSelected(item) {
            if (this.isSoldOut(item)) return;
            this.$emit('item-selected', item);
        },
        highlightedName(name) {
            if (!this.searchQuery) return name;
            const regex = new RegExp(`(${this.searchQuery})`, 'gi');
            return name.replace(regex, '<strong style="color: #9baec8">$1</strong>');
        },
        truncatedName(name) {
            return name.length > 10 ? name.substring(0, 10) + '...' : name;
        },
        handleCancel() {
            this.searchQuery = null;
            this.isSearching = false;
            this.$emit('setBarcodeActive', true);
        },
        handleFocus() {
            this.isSearching = true;
            this.$emit('setBarcodeActive', false);
        },
        handleBlur() {
            setTimeout(() => {
                this.isSearching = false;
                this.searchQuery = null;
                this.$emit('setBarcodeActive', true);
            }, 200);

        },
        updateSearchQuery(event) {
            this.searchQuery = event.target.value;
            this.searchItems();
        },
        handleGroceryIconTap() {
            if (!this.groceryCheckoutModeEnabled) return;
            this.scannerTapCount += 1;
            if (this.scannerTapCount >= 10) {
                this.itemListReady = false;
                this.showMainItemListForGrocery = true;
                this.scannerTapCount = 0;
                setTimeout(() => { this.itemListReady = true; }, 600);
            }
        },
        closeGroceryItemList() {
            this.showMainItemListForGrocery = false;
            this.scannerTapCount = 0;
            this.isSearching = false;
            this.searchQuery = '';
            this.$emit('setBarcodeActive', true);
        },
        searchItems() {
            this.filteredItems = this.itemsToSearch
                .filter(item => item.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name))
                .slice(0, 100);
        }
    }
};
</script>
<style scoped>
.top-bar {
    display: flex;
    align-items: center;
    padding: 2.125rem 1.375rem 1rem 1.375rem;
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.9);
    gap: 1rem;
}

.search {
    display: flex;
    align-items: center;
    position: relative; /* Ensure the container is positioned relatively */
}

.search-icon {
    position: absolute;
    left: 1rem; /* Adjust as needed for spacing */
    top: 50%;
    transform: translateY(-50%); /* Center vertically */
    margin: 0; /* Remove any additional margins */
}

.search-bar {
    flex-grow: 1;
    width: 100%; /* Use full width */
    padding-left: 3rem; /* Add padding to the left to accommodate the icon */
    border: 0.125rem solid #2A2927;
    font-size: 1.75rem !important;
    margin: 0;
    line-height: 1.25rem;
    padding: 1rem 2rem 1rem 3rem; /* Adjust padding */
    color: rgba(255, 255, 255, 0.64);
    background: #000;
    font-weight: 400;
    border-radius: 6.25rem;
    margin-right: 2rem;
    transition: width 0.3s ease, border-color 0.3s ease;
    /* Add transition for width */
}


.search-bar:focus {
    outline: none;
    border: 0.125rem solid #fff !important;
    color: #fff;
}

.search-bar.search-active {
    width: 30rem;
    /* Adjust as needed */
}

.button-group {
    border: 0.125rem solid #2A2927;
    border-radius: 1rem;
    padding: 0.25rem;
}

.button-group button {
    font-size: 1.5rem;
    border: none;
    font-weight: 700;
    border-radius: 0.75rem;
    line-height: 1.75rem;
    color: #fff;
    background-color: #000;
    padding: 1.125rem 1.625rem;
}

.button-group button.selected {
    background: #424659;
}

.grocery-close-button {
    margin-left: auto;
    font-size: 1rem;
    font-weight: 700;
    border: 0.125rem solid #2A2927;
    border-radius: 0.75rem;
    color: #fff;
    background: #000;
    padding: 0.75rem 1.25rem;
}

.item-list {
    margin: 1.25rem;
    flex: 1;
    height: 100vh;
    overflow-y: scroll;
    display: grid;
    align-items: flex-start;
    grid-template-columns: repeat(auto-fill, minmax(12.5rem, 1fr));
    gap: 1rem;
}

.grocery-mode-panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 1rem;
    padding: 2rem;
}

.grocery-mode-icon-button {
    border: none;
    background: transparent;
    color: #424659;
    padding: 0.5rem;
}

.grocery-mode-panel h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0;
}

.grocery-mode-panel p {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.64);
    margin: 0;
}

.item-list h2 {
    font-size: 2rem;
}

.item-list .item {
    cursor: pointer;
    border: 1px solid #34383c;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    transition: transform 0.3s ease, background-color 0.3s ease;
    height: 15.625rem;
    border-radius: 1rem;
}

.item-list .item:not(.sold-out):hover {
    background-color: #2a2c30;
    transform: translateY(-5px);
}

.item-list .item.sold-out {
    pointer-events: none;
    cursor: default;
    opacity: 0.7;
    filter: grayscale(100%);
}

.item-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
}

.item-image-bg {
    width: 100%;
    height: 9.375rem;
    /* Adjust the height as needed */
    background-size: cover;
    background-position: center;
    margin-bottom: 1rem;
}

.color-block {
    background-color: #2b2c2e;
    /* Replace with desired color */
    display: flex;
    width: 100%;
    height: 9.375rem;
    /* Same height as the image for consistency */
    margin-bottom: 1rem;
    font-weight: 700;
    font-size: 1.4rem;
}

.color-block .item-name-block {
    font-size: 1.375rem;
    line-height: 1.5rem;
    width: 10rem;
    text-overflow: ellipsis;
    font-weight: 700;
    text-align: left;
    margin: 2rem;
    margin: auto;
}

.item.no-image {
    text-align: left;
}

.item.no-image .item-image {
    display: none;

}

.item-list .item .item-name {
    font-weight: bold;
    padding: 0rem 1rem 0.5rem 1rem;
    height: auto;
    /* Allow height to adjust based on content */
    word-break: break-word;
    color: #e6e8eb;
    text-align: left;
    /* Left align text */
    white-space: normal;
    /* Allow text to wrap */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    /* Limit to 2 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.item-list .item .item-price {
    color: #9baec8;
    font-weight: 700;
    padding: 0 1rem;
    margin-top: auto;
    /* Push price to the bottom */
    text-align: right;
    /* Align price to the right */
}

.item-list .item .item-name strong {
    color: #9baec8 !important;
}


.color-block {
    background-color: #2b2c2e;
    /* Replace with desired color */
    display: flex;
    width: 100%;
    height: 9.375rem;
    /* Same height as the image for consistency */
    margin-bottom: 1rem;
    font-weight: 700;
    font-size: 1.4rem;
}

.color-block .item-name-block {
    font-size: 1.375rem;
    line-height: 1.5rem;
    width: 10rem;
    text-overflow: ellipsis;
    font-weight: 700;
    text-align: left;
    margin: 2rem;
    margin: auto;
}

</style>
